import { organizationsApi } from "api";
import type { ExtensionElements } from "model/processModel";
import {
  SLA_OWNER_CATEGORY_ORDER,
  type SlaEditorActorOption,
  type SlaOwnerCategory,
  type SlaOwnerOption,
} from "model/slaEditorModel";
import { useEffect, useMemo, useState } from "react";
import { getResourceUsers } from "utils/resourceUserAssociations";

/**
 * Utenti selezionabili come **responsabile** dello SLA nel designer
 * (2026-10-08). Solo endpoint gia' esistenti:
 * - amministratori: `GET organizations/{id}/admins` (`organizations_users.administrator`);
 * - capi reparto: `GET groups/{id}/users` per ogni gruppo dell'organizzazione,
 *   filtrando `hod` (`users_groups.hod`). Non esiste una lista dei capi reparto
 *   per organizzazione: una chiamata per gruppo;
 * - partecipanti del processo: extension elements delle lane del modello
 *   (`idparticipanttype` 3 = utente diretto, 2 = ruolo -> `GET roles/{id}/users`,
 *   1 = gruppo -> `GET groups/{id}/users`). I robot (4) sono esclusi.
 *
 * Le liste dei membri sono tenute per id: aggiungere un partecipante a una lane
 * legge solo il ruolo/gruppo nuovo. Una lettura fallita vale come lista vuota
 * (nessun caricamento infinito).
 */
interface MemberUser {
  id: number;
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  email?: string | null;
  hod?: boolean | null;
}

const PARTICIPANT_GROUP = 1;
const PARTICIPANT_ROLE = 2;
const PARTICIPANT_USER = 3;

export const slaMemberDisplayName = (user: MemberUser): string => {
  const fullName = [user.firstname, user.lastname]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return fullName || (user.username ?? "").trim() || (user.email ?? "").trim() || `#${user.id}`;
};

const idsKey = (ids: number[]): string =>
  Array.from(new Set(ids))
    .sort((a, b) => a - b)
    .join(",");

const parseIdsKey = (key: string): number[] =>
  key ? key.split(",").map(Number) : [];

const usersOf = (response: { data?: { users?: MemberUser[] } }): MemberUser[] =>
  response?.data?.users && Array.isArray(response.data.users) ? response.data.users : [];

/** Legge i membri delle risorse mancanti in `cache` e li aggiunge. */
const useResourceMembers = (
  resource: "groups" | "roles",
  enabled: boolean,
  key: string,
) => {
  const [members, setMembers] = useState<Record<number, MemberUser[]>>({});

  useEffect(() => {
    if (!enabled) return;
    const missing = parseIdsKey(key).filter((id) => !(id in members));
    if (missing.length === 0) return;
    let isMounted = true;
    (async () => {
      const results = await Promise.allSettled(
        missing.map((id) => getResourceUsers(resource, id)),
      );
      if (!isMounted) return;
      setMembers((current) => {
        const next = { ...current };
        missing.forEach((id, index) => {
          const result = results[index];
          next[id] = result.status === "fulfilled" ? usersOf(result.value) : [];
        });
        return next;
      });
    })();
    return () => {
      isMounted = false;
    };
    // `members` e' letto solo per calcolare i mancanti: rileggere a ogni
    // aggiornamento della cache non serve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, resource]);

  const loading = enabled && parseIdsKey(key).some((id) => !(id in members));
  return { members, loading };
};

export interface SlaOwnerOptionsParams {
  enabled: boolean;
  idorganization: number | null;
  /** Gruppi dell'organizzazione, per i capi reparto. */
  groupIds: number[];
  /** Assegnazioni delle lane del modello; `null` = non disponibili. */
  participants: ExtensionElements | null;
  /** Utenti dell'organizzazione gia' letti dal designer: nomi degli assegnati diretti. */
  knownUsers: SlaEditorActorOption[];
}

export interface SlaOwnerOptionsValue {
  options: SlaOwnerOption[];
  loading: boolean;
}

export const useSlaOwnerOptions = ({
  enabled,
  idorganization,
  groupIds,
  participants,
  knownUsers,
}: SlaOwnerOptionsParams): SlaOwnerOptionsValue => {
  const [admins, setAdmins] = useState<MemberUser[] | null>(null);

  useEffect(() => {
    if (!enabled || !idorganization) {
      setAdmins(null);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const response = await organizationsApi.get(`${idorganization}/admins`);
        if (isMounted) setAdmins(usersOf(response));
      } catch {
        if (isMounted) setAdmins([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [enabled, idorganization]);

  const assignments = useMemo(
    () => Object.values(participants ?? {}).flat(),
    [participants],
  );
  const participantGroupIds = assignments
    .filter((item) => item.idparticipanttype === PARTICIPANT_GROUP)
    .map((item) => item.idparticipant);
  const participantRoleIds = assignments
    .filter((item) => item.idparticipanttype === PARTICIPANT_ROLE)
    .map((item) => item.idparticipant);

  const groupsKey = idsKey([...groupIds, ...participantGroupIds]);
  const rolesKey = idsKey(participantRoleIds);
  const participantGroupsKey = idsKey(participantGroupIds);

  const groups = useResourceMembers("groups", enabled, groupsKey);
  const roles = useResourceMembers("roles", enabled, rolesKey);

  const options = useMemo<SlaOwnerOption[]>(() => {
    const byId = new Map<number, SlaOwnerOption>();
    const rank = (category: SlaOwnerCategory) => SLA_OWNER_CATEGORY_ORDER.indexOf(category);
    const add = (id: number, name: string, category: SlaOwnerCategory) => {
      if (!Number.isFinite(id) || id <= 0) return;
      const existing = byId.get(id);
      if (!existing || rank(category) < rank(existing.category)) {
        byId.set(id, { id, name: existing?.name || name, category });
      }
    };

    (admins ?? []).forEach((user) => add(user.id, slaMemberDisplayName(user), "admin"));
    Object.values(groups.members)
      .flat()
      .filter((user) => user.hod)
      .forEach((user) => add(user.id, slaMemberDisplayName(user), "hod"));

    assignments
      .filter((item) => item.idparticipanttype === PARTICIPANT_USER)
      .forEach((item) => {
        // Il nome dell'assegnazione e' quello mostrato nella lane; il
        // cognome si aggiunge solo se non e' gia' contenuto.
        const laneName = [item.name, item.lastname]
          .map((part) => (part ?? "").trim())
          .filter(Boolean)
          .reduce((acc, part) => (acc.includes(part) ? acc : `${acc} ${part}`.trim()), "");
        const known = knownUsers.find((user) => user.id === item.idparticipant);
        add(
          item.idparticipant,
          known?.name || laneName || slaMemberDisplayName({ id: item.idparticipant }),
          "participant",
        );
      });
    parseIdsKey(rolesKey).forEach((id) =>
      (roles.members[id] ?? []).forEach((user) =>
        add(user.id, slaMemberDisplayName(user), "participant"),
      ),
    );
    parseIdsKey(participantGroupsKey).forEach((id) =>
      (groups.members[id] ?? []).forEach((user) =>
        add(user.id, slaMemberDisplayName(user), "participant"),
      ),
    );

    return Array.from(byId.values()).sort(
      (a, b) => rank(a.category) - rank(b.category) || a.name.localeCompare(b.name),
    );
  }, [
    admins,
    groups.members,
    roles.members,
    assignments,
    rolesKey,
    participantGroupsKey,
    knownUsers,
  ]);

  return {
    options,
    loading: enabled && (admins === null || groups.loading || roles.loading),
  };
};

/**
 * Opzioni del selettore per una bozza: se il responsabile salvato non e' tra
 * gli ammessi resta in lista (categoria `unavailable`), con il nome preso
 * dagli utenti dell'organizzazione quando noto.
 */
export const slaOwnerOptionsForDraft = (
  options: SlaOwnerOption[],
  currentUserId: number | null,
  knownUsers: SlaEditorActorOption[],
): SlaOwnerOption[] => {
  if (currentUserId === null || options.some((option) => option.id === currentUserId)) {
    return options;
  }
  const known = knownUsers.find((user) => user.id === currentUserId);
  return [
    ...options,
    { id: currentUserId, name: known?.name || `#${currentUserId}`, category: "unavailable" },
  ];
};
